import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Gift, Siren, Wallet, ArrowClockwise, ArrowsClockwise, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function MyVouchersPage() {
  const [vouchers, setVouchers] = useState([]);
  const [ledger, setLedger] = useState({ balance: null, entries: [] });
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComplaint, setShowComplaint] = useState(false);
  const [activeVoucher, setActiveVoucher] = useState(null);
  const [form, setForm] = useState({ complaint_type: 'used', details: '' });
  // Sub-staff forwarding
  const [me, setMe] = useState(null);
  const [subStaff, setSubStaff] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showForward, setShowForward] = useState(false);
  const [forwardTo, setForwardTo] = useState('');
  const [forwarding, setForwarding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, l, c, m, s] = await Promise.all([
        api.get('/voucher-stock/my-assigned-vouchers'),
        api.get('/voucher-stock/my-voucher-ledger'),
        api.get('/voucher-stock/my-complaints'),
        api.get('/auth/me'),
        api.get('/staff/my-substaff').catch(() => ({ data: [] })),
      ]);
      setVouchers(v.data || []);
      setLedger(l.data);
      setComplaints(c.data || []);
      setMe(m.data || null);
      // /staff/my-substaff already returns only direct sub_staff under me
      setSubStaff(s.data || []);
      setSelectedIds(new Set());
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openComplaint = (v) => { setActiveVoucher(v); setForm({ complaint_type: 'used', details: '' }); setShowComplaint(true); };
  const raise = async () => {
    try {
      const r = await api.post('/voucher-stock/complaints', {
        voucher_id: activeVoucher.id,
        complaint_type: form.complaint_type,
        details: form.details,
      });
      toast.success(r.data.message);
      setShowComplaint(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const isStaffLead = me?.role === 'staff';
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllAvailable = (ids) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };
  const submitForward = async () => {
    if (!forwardTo) { toast.error('Sub-staff select karo'); return; }
    if (selectedIds.size === 0) { toast.error('Vouchers select karo'); return; }
    setForwarding(true);
    try {
      const r = await api.post('/voucher-stock/staff-reassign', {
        voucher_ids: Array.from(selectedIds),
        staff_id: forwardTo,
      });
      toast.success(r.data.message);
      setShowForward(false);
      setForwardTo('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Forward failed');
    } finally { setForwarding(false); }
  };

  const available = vouchers.filter((v) => v.status === 'assigned' && !v.has_complaint);
  const soldOrFlagged = vouchers.filter((v) => v.status !== 'assigned' || v.has_complaint);

  return (
    <div className="space-y-5" data-testid="my-vouchers-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Gift size={26} weight="duotone" className="text-fuchsia-600" /> My Voucher Basket
        </h1>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load}><ArrowClockwise size={14} /> Refresh</Button>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-emerald-700 font-semibold">Available Vouchers</div>
          <div className="text-xl font-bold text-emerald-900">{available.length}</div>
          <div className="text-[10px] text-emerald-600">{fmt(available.reduce((s, x) => s + x.face_value, 0))}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-blue-700 font-semibold">Total Assigned (All Time)</div>
          <div className="text-xl font-bold text-blue-900">{vouchers.length}</div>
          <div className="text-[10px] text-blue-600">{fmt(vouchers.reduce((s, x) => s + x.face_value, 0))}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-amber-700 font-semibold flex items-center gap-1"><Siren size={12} /> My Complaints Raised</div>
          <div className="text-xl font-bold text-amber-900">{complaints.length}</div>
          <div className="text-[10px] text-amber-600">Open: {complaints.filter((c) => c.status === 'open' || c.status === 'under_review').length}</div>
        </div>
        <div className={`border rounded-lg p-3 ${ledger.balance?.balance < 0 ? 'bg-red-50 border-red-200' : 'bg-indigo-50 border-indigo-200'}`}>
          <div className={`text-[11px] uppercase font-semibold flex items-center gap-1 ${ledger.balance?.balance < 0 ? 'text-red-700' : 'text-indigo-700'}`}><Wallet size={12} /> My Voucher Ledger</div>
          <div className={`text-xl font-bold ${ledger.balance?.balance < 0 ? 'text-red-900' : 'text-indigo-900'}`}>{fmt(ledger.balance?.balance || 0)}</div>
          <div className="text-[10px] opacity-80">Dr {fmt(ledger.balance?.debit || 0)} / Cr {fmt(ledger.balance?.credit || 0)}</div>
        </div>
      </div>

      {/* Assigned Vouchers */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-wrap">
          <div className="font-semibold text-gray-900 flex items-center gap-2">
            <Gift size={18} className="text-fuchsia-600" />
            My Vouchers ({vouchers.length})
          </div>
          {isStaffLead && subStaff.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-xs text-gray-500">{selectedIds.size} selected</span>
              <Button
                size="sm"
                disabled={selectedIds.size === 0}
                onClick={() => setShowForward(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 gap-1"
                data-testid="forward-to-substaff-btn"
              >
                <ArrowsClockwise size={14} /> Forward to Sub-Staff ({selectedIds.size})
              </Button>
              {selectedIds.size > 0 && (
                <Button size="sm" variant="outline" className="text-xs h-8 gap-1" onClick={() => setSelectedIds(new Set())}>
                  <X size={12} /> Clear
                </Button>
              )}
            </div>
          )}
        </div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : vouchers.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">Koi voucher abhi tak assign nahi hua</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  {isStaffLead && subStaff.length > 0 && (
                    <th className="px-3 py-2 text-center w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all available vouchers"
                        checked={available.length > 0 && available.every(v => selectedIds.has(v.id))}
                        onChange={() => selectAllAvailable(available.map(v => v.id))}
                        data-testid="select-all-vouchers"
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">PIN</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Assigned On</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => {
                  const canPick = isStaffLead && subStaff.length > 0 && v.status === 'assigned' && !v.has_complaint;
                  return (
                  <tr key={v.id} className="border-t hover:bg-gray-50" data-testid={`my-voucher-${v.id}`}>
                    {isStaffLead && subStaff.length > 0 && (
                      <td className="px-3 py-2 text-center">
                        {canPick ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(v.id)}
                            onChange={() => toggleSelect(v.id)}
                            data-testid={`pick-${v.id}`}
                          />
                        ) : null}
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono text-xs">{v.code}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">{v.pin || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(v.face_value)}</td>
                    <td className="px-3 py-2 text-xs">{v.vendor_name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                        v.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                        v.status === 'sold' ? 'bg-indigo-100 text-indigo-700' :
                        v.status === 'complaint' ? 'bg-amber-100 text-amber-700' :
                        v.status === 'reversed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{v.status?.toUpperCase()}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">{fmtDate(v.assigned_at)}</td>
                    <td className="px-3 py-2 text-right">
                      {v.status === 'assigned' && !v.has_complaint && (
                        <Button size="sm" variant="outline" className="text-red-600 h-7 gap-1" onClick={() => openComplaint(v)} data-testid={`my-complaint-${v.id}`}>
                          <Siren size={12} /> Raise Complaint
                        </Button>
                      )}
                      {v.has_complaint && <span className="text-[10px] text-amber-600 font-semibold">Complaint raised</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* My Complaints with Timeline */}
      {complaints.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b font-semibold text-gray-900 flex items-center gap-2">
            <Siren size={18} className="text-red-600" /> My Complaints ({complaints.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Ticket</th>
                  <th className="px-3 py-2 text-left">Voucher</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Resolution</th>
                </tr>
              </thead>
              <tbody>
                {complaints.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs font-bold text-indigo-700">{c.ticket_no}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.voucher_code}</td>
                    <td className="px-3 py-2 text-xs">{c.complaint_type}</td>
                    <td className="px-3 py-2 text-right">{fmt(c.credited_amount)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                        c.status === 'resolved' ? (c.in_favor_of === 'staff' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700') :
                        c.status === 'under_review' ? 'bg-orange-100 text-orange-700' :
                        c.status === 'reopened' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{c.status?.replace('_', ' ').toUpperCase()}</span>
                      {c.in_favor_of && <div className="text-[9px] text-gray-500 mt-0.5">In favor: {c.in_favor_of}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-[280px]">{c.resolution_notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ledger */}
      {ledger.entries?.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b font-semibold text-gray-900 flex items-center gap-2">
            <Wallet size={18} className="text-indigo-600" /> My Voucher Ledger
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Ticket</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="px-3 py-2 text-xs">{fmtDate(e.created_at)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${e.type === 'credit' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {e.type === 'credit' ? 'CR (+)' : 'DR (−)'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{e.category}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${e.type === 'credit' ? 'text-emerald-700' : 'text-red-700'}`}>{e.type === 'credit' ? '+' : '−'} {fmt(e.amount)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.ticket_no || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-[280px] truncate">{e.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Raise Complaint Dialog */}
      <Dialog open={showComplaint} onOpenChange={setShowComplaint}>
        <DialogContent>
          <DialogHeader><DialogTitle>Raise Complaint</DialogTitle></DialogHeader>
          {activeVoucher && (
            <div className="space-y-3">
              <div className="text-xs p-2 bg-gray-50 rounded">
                <div><span className="text-gray-500">Code:</span> <span className="font-mono">{activeVoucher.code}</span></div>
                <div><span className="text-gray-500">Amount:</span> {fmt(activeVoucher.face_value)}</div>
                <div><span className="text-gray-500">Vendor:</span> {activeVoucher.vendor_name}</div>
              </div>
              <div>
                <label className="text-xs font-semibold">Complaint Type *</label>
                <select className="w-full h-10 px-3 border rounded-md text-sm" value={form.complaint_type}
                  onChange={(e) => setForm({ ...form, complaint_type: e.target.value })}>
                  <option value="used">Already Used</option>
                  <option value="invalid">Invalid Code</option>
                  <option value="expired">Expired</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold">Details *</label>
                <textarea rows={3} className="w-full px-3 py-2 border rounded text-sm"
                  value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
                  placeholder="Kya issue aaya, kis screen pe error dikha, etc." />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded">
                ⚠️ Ticket raise karne par vendor ko real-time alert jayega. Aapke aur vendor ke ledger me {fmt(activeVoucher.credited_amount)} temporarily debit ho jayega. Admin resolve karega.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplaint(false)}>Cancel</Button>
            <Button onClick={raise} className="bg-red-600 hover:bg-red-700" disabled={!form.details.trim()} data-testid="staff-raise-complaint-btn">
              Raise Complaint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward to Sub-Staff Dialog */}
      <Dialog open={showForward} onOpenChange={setShowForward}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowsClockwise size={18} className="text-blue-600" /> Forward Vouchers to Sub-Staff
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[12px] text-blue-800 bg-blue-50 p-2 rounded">
              {selectedIds.size} voucher selected. Forward karne ke baad ye vouchers <b>aapke basket se hat ke</b> sub-staff ke basket me chale jayenge.
            </div>
            <div>
              <label className="text-xs font-semibold">Sub-Staff *</label>
              <select
                value={forwardTo}
                onChange={(e) => setForwardTo(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
                data-testid="forward-substaff-select"
              >
                <option value="">— Select Sub-Staff —</option>
                {subStaff.map(s => (
                  <option key={s.id} value={s.id}>{s.name} · {s.email}</option>
                ))}
              </select>
              {subStaff.length === 0 && (
                <div className="text-[11px] text-amber-700 mt-1">Aapke under koi sub-staff abhi nahi hai. Admin se add karwao.</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForward(false)} disabled={forwarding}>Cancel</Button>
            <Button
              onClick={submitForward}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={forwarding || !forwardTo || selectedIds.size === 0}
              data-testid="confirm-forward-btn"
            >
              {forwarding ? 'Forwarding…' : `Forward ${selectedIds.size} Voucher${selectedIds.size === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
